import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { salesActivityService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";

const TYPES = ["Call", "Meeting", "Email", "Demo", "Note", "Other"];
const STATUSES = ["Planned", "Completed"];
const EMPTY = { type: "Call", subject: "", notes: "", outcome: "", status: "Completed", relatedType: "", relatedId: "", tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function SalesActivitiesPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await salesActivityService.list({ type: typeFilter || undefined, size: 100 }); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setForm(EMPTY); setOpen(true); };

  const save = async () => {
    if (!form.subject) return toast.error("Subject is required");
    if (isSuper && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await salesActivityService.create(payload);
      toast.success("Activity logged"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this activity?")) return;
    try { await salesActivityService.remove(r.id); toast.success("Activity deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("SALES_ACTIVITY.CREATE");
  const canDelete = hasPermission("SALES_ACTIVITY.DELETE");

  const columns = [
    { key: "type", header: "Type", render: (r) => <Badge color="blue">{r.type}</Badge> },
    { key: "subject", header: "Subject", render: (r) => <span className="font-medium text-foreground">{r.subject}</span> },
    { key: "relatedType", header: "Related To", render: (r) => r.relatedType ? `${r.relatedType}` : "—" },
    { key: "status", header: "Status", render: (r) => <Badge color={r.status === "Completed" ? "green" : "amber"}>{r.status}</Badge> },
    { key: "outcome", header: "Outcome", render: (r) => <span className="text-muted-foreground text-xs">{r.outcome || "—"}</span> },
    {
      key: "_a", header: "",
      render: (r) => canDelete && <div className="flex justify-end"><button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`sa-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button></div>,
    },
  ];

  return (
    <div data-testid="sales-activities-page">
      <PageHeader title="Sales Activities" subtitle="Log calls, meetings, emails and demos (no real send in this phase)"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="sa-create-btn">
            <Plus className="h-4 w-4" /> Log Activity
          </button>
        )}
      />
      <div className="flex items-center gap-2 mb-4">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="sa-type-filter">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <DataTable columns={columns} rows={rows} loading={loading} testid="sales-activities-table" empty="No activities logged yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="sa-dialog">
            <h3 className="text-lg font-bold mb-4">Log Sales Activity</h3>
            <div className="space-y-3.5">
              {isSuper && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="sa-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type"><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ciq-input" data-testid="sa-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
                <Field label="Status"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="ciq-input">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
              </div>
              <Field label="Subject *"><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="ciq-input" data-testid="sa-subject" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Related Type"><select value={form.relatedType} onChange={(e) => setForm({ ...form, relatedType: e.target.value })} className="ciq-input"><option value="">—</option>{["Lead", "Opportunity", "Customer"].map((t) => <option key={t}>{t}</option>)}</select></Field>
                <Field label="Related ID"><input value={form.relatedId} onChange={(e) => setForm({ ...form, relatedId: e.target.value })} className="ciq-input" placeholder="optional" /></Field>
              </div>
              <Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ciq-input" rows={2} /></Field>
              <Field label="Outcome"><input value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} className="ciq-input" /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="sa-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
